/* eslint-disable no-unused-vars */
import {
  Box,
  Typography,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import DateTimePicker from './date-time-picker';
import { getAllAccessorials, getServicesActive } from 'src/queries/query';
import StopsComponent from './StopsComponent';
import { format } from 'date-fns';
import CommiditiesTable from './CommiditiesTable';
import { useQuery } from '@tanstack/react-query';
import CustomSelect from 'src/components/custom-select';
import AddressAutocomplete from './shipper-autocomplete';

const timeOptions = [
  '08:00',
  '08:15',
  '08:30',
  '08:45',
  '09:00',
  '09:15',
  '09:30',
  '09:45',
  '10:00',
  '10:15',
  '10:30',
  '10:45',
  '11:00',
  '11:15',
  '11:30',
  '11:45',
  '12:00',
  '12:15',
  '12:30',
  '12:45',
  '13:00',
  '13:15',
  '13:30',
  '13:45',
  '14:00',
  '14:15',
  '14:30',
  '14:45',
  '15:00',
  '15:15',
  '15:30',
  '15:45',
  '16:00',
  '16:15',
  '16:30',
  '16:45',
  '17:00',
  '17:15',
  '17:30',
  '17:45',
  '18:00',
  '18:15',
  '18:30',
  '18:45',
  '19:00',
  '19:15',
  '19:30',
  '19:45',
  '20:00',
  '20:15',
  '20:30',
  '20:45',
];

// Shipper Information Section Component
const ShipperInformationSection = ({
  values,
  customersAddresses,
  handleNestedChange,
  handleAddressChange,
  setFieldValue,
  touched,
  errors,
}) => (
  <Box>
    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
      Shipper Information
    </Typography>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      Quick search
    </Typography>
    <Box sx={{ mb: 2 }}>
      <AddressAutocomplete
        customers={customersAddresses}
        values={values}
        handleNestedChange={handleNestedChange}
        handleAddressChange={handleAddressChange}
        setFieldValue={setFieldValue}
        fieldName="shipper"
      />
    </Box>
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Company name"
          required
          name="shipper.name"
          value={values.shipper?.name || values.shipper?.company || ''}
          onChange={(e) =>
            handleNestedChange('shipper', 'name', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Address 1"
          required
          name="shipper.addresses.address"
          value={
            values.shipper?.addresses?.address ||
            values.shipper?.address?.address ||
            ''
          }
          onChange={(e) =>
            handleAddressChange('shipper', 'address', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Address 2"
          name="shipper.addresses.address2"
          value={
            values?.shipper?.addresses?.address2 ||
            values?.shipper?.address?.address2 ||
            ''
          }
          onChange={(e) =>
            handleAddressChange('shipper', 'address2', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          label="City"
          required
          name="shipper.addresses.city"
          value={
            values?.shipper?.addresses?.city ||
            values?.shipper?.address?.city ||
            ''
          }
          onChange={(e) =>
            handleAddressChange('shipper', 'city', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          label="State"
          required
          name="shipper.addresses.state"
          value={
            values?.shipper?.addresses?.state ||
            values?.shipper?.address?.state ||
            ''
          }
          onChange={(e) =>
            handleAddressChange('shipper', 'state', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          label="Country"
          required
          name="shipper.addresses.country"
          value={
            values?.shipper?.addresses?.country ||
            values?.shipper?.address?.country ||
            ''
          }
          onChange={(e) =>
            handleAddressChange('shipper', 'country', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          label="ZIP code"
          required
          name="shipper.addresses.postal"
          value={
            values?.shipper?.addresses?.postal ||
            values?.shipper?.address?.postal ||
            ''
          }
          onChange={(e) =>
            handleAddressChange('shipper', 'postal', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          name="shipper.addresses.latitude"
          label="Latitude"
          required
          value={
            values?.shipper?.addresses?.latitude ||
            values?.shipper?.address?.latitude ||
            ''
          }
          onChange={(e) =>
            handleAddressChange('shipper', 'latitude', e.target.value)
          }
          size="small"
          variant="outlined"
          error={
            touched?.shipper?.addresses?.latitude &&
            Boolean(errors?.shipper?.addresses?.latitude)
          }
          helperText={
            touched?.shipper?.addresses?.latitude &&
            errors?.shipper?.addresses?.latitude
          }
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          name="shipper.addresses.longitude"
          label="Longitude"
          required
          value={
            values?.shipper?.addresses?.longitude ||
            values?.shipper?.address?.longitude ||
            ''
          }
          onChange={(e) =>
            handleAddressChange('shipper', 'longitude', e.target.value)
          }
          size="small"
          variant="outlined"
          error={
            touched?.shipper?.addresses?.longitude &&
            Boolean(errors?.shipper?.addresses?.longitude)
          }
          helperText={
            touched?.shipper?.addresses?.longitude &&
            errors?.shipper?.addresses?.longitude
          }
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Shipper notes"
          name="shipper.notes"
          value={values?.shipper?.notes || values?.shipper?.shipperNotes || ''}
          onChange={(e) =>
            handleNestedChange('shipper', 'notes', e.target.value)
          }
          placeholder="Type any notes here"
          multiline
          rows={4}
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={6}>
        <CustomSelect
          label="Opening time"
          name="shipper.openingTime"
          value={
            values?.shipper?.openingTime ||
            values?.shipper?.businessHours?.open ||
            ''
          }
          onChange={(field, value) =>
            handleNestedChange('shipper', 'openingTime', value)
          }
          options={timeOptions.map((time) => ({
            id: time,
            value: time,
            label: time,
          }))}
          size="small"
          error={
            touched?.shipper?.openingTime &&
            Boolean(errors?.shipper?.openingTime)
          }
          errorMessage={
            touched?.shipper?.openingTime &&
            Boolean(errors?.shipper?.openingTime)
              ? errors?.shipper?.openingTime
              : undefined
          }
        />
      </Grid>
      <Grid item xs={6}>
        <CustomSelect
          label="Closing time"
          name="shipper.closingTime"
          value={
            values?.shipper?.closingTime ||
            values?.shipper?.businessHours?.close ||
            ''
          }
          onChange={(field, value) =>
            handleNestedChange('shipper', 'closingTime', value)
          }
          options={timeOptions.map((time) => ({
            id: time,
            value: time,
            label: time,
          }))}
          size="small"
          error={
            touched?.shipper?.closingTime &&
            Boolean(errors?.shipper?.closingTime)
          }
          errorMessage={
            touched?.shipper?.closingTime &&
            Boolean(errors?.shipper?.closingTime)
              ? errors?.shipper?.closingTime
              : undefined
          }
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Contact name"
          name="shipper.contactName"
          value={values.shipper?.contactName || ''}
          onChange={(e) =>
            handleNestedChange('shipper', 'contactName', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={8}>
        <TextField
          fullWidth
          label="Phone"
          name="shipper.contactPhone"
          value={values.shipper?.contactPhone || values.shipper?.phone || ''}
          onChange={(e) =>
            handleNestedChange('shipper', 'contactPhone', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          fullWidth
          label="Ext."
          name="shipper.contactExt"
          value={values.shipper?.contactExt || values.shipper?.phoneExt || ''}
          onChange={(e) =>
            handleNestedChange('shipper', 'contactExt', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Contact email"
          name="shipper.contactEmail"
          value={values.shipper?.contactEmail || values.shipper?.email || ''}
          onChange={(e) =>
            handleNestedChange('shipper', 'contactEmail', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
    </Grid>
  </Box>
);

// Consignee Information Section Component
const ConsigneeInformationSection = ({
  values,
  customersAddresses,
  handleNestedChange,
  handleAddressChange,
  setFieldValue,
  touched,
  errors,
}) => (
  <Box>
    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
      Consignee Information
    </Typography>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      Quick search
    </Typography>
    <Box sx={{ mb: 2 }}>
      <AddressAutocomplete
        customers={customersAddresses}
        values={values}
        handleNestedChange={handleNestedChange}
        handleAddressChange={handleAddressChange}
        setFieldValue={setFieldValue}
        fieldName="consignee"
      />
    </Box>
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Company name"
          required
          name="consignee.name"
          value={values.consignee?.name || values.consignee?.company || ''}
          onChange={(e) =>
            handleNestedChange('consignee', 'name', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Address 1"
          required
          name="consignee.addresses.address"
          value={
            values.consignee?.addresses?.address ||
            values.consignee?.address?.address ||
            ''
          }
          onChange={(e) =>
            handleAddressChange('consignee', 'address', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Address 2"
          name="consignee.addresses.address2"
          value={
            values?.consignee?.addresses?.address2 ||
            values?.consignee?.address?.address2 ||
            ''
          }
          onChange={(e) =>
            handleAddressChange('consignee', 'address2', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          label="City"
          required
          name="consignee.addresses.city"
          value={
            values?.consignee?.addresses?.city ||
            values?.consignee?.address?.city ||
            ''
          }
          onChange={(e) =>
            handleAddressChange('consignee', 'city', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          label="State"
          required
          name="consignee.addresses.state"
          value={
            values?.consignee?.addresses?.state ||
            values?.consignee?.address?.state ||
            ''
          }
          onChange={(e) =>
            handleAddressChange('consignee', 'state', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          label="Country"
          required
          name="consignee.addresses.country"
          value={
            values?.consignee?.addresses?.country ||
            values?.consignee?.address?.country ||
            ''
          }
          onChange={(e) =>
            handleAddressChange('consignee', 'country', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          label="ZIP code"
          required
          name="consignee.addresses.postal"
          value={
            values?.consignee?.addresses?.postal ||
            values?.consignee?.address?.postal ||
            ''
          }
          onChange={(e) =>
            handleAddressChange('consignee', 'postal', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          name="consignee.addresses.latitude"
          label="Latitude"
          required
          value={
            values?.consignee?.addresses?.latitude ||
            values?.consignee?.address?.latitude ||
            ''
          }
          onChange={(e) =>
            handleAddressChange('consignee', 'latitude', e.target.value)
          }
          size="small"
          variant="outlined"
          error={
            touched?.consignee?.addresses?.latitude &&
            Boolean(errors?.consignee?.addresses?.latitude)
          }
          helperText={
            touched?.consignee?.addresses?.latitude &&
            errors?.consignee?.addresses?.latitude
          }
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          name="consignee.addresses.longitude"
          label="Longitude"
          required
          value={
            values?.consignee?.addresses?.longitude ||
            values?.consignee?.address?.longitude ||
            ''
          }
          onChange={(e) =>
            handleAddressChange('consignee', 'longitude', e.target.value)
          }
          size="small"
          variant="outlined"
          error={
            touched?.consignee?.addresses?.longitude &&
            Boolean(errors?.consignee?.addresses?.longitude)
          }
          helperText={
            touched?.consignee?.addresses?.longitude &&
            errors?.consignee?.addresses?.longitude
          }
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Consignee notes"
          name="consignee.notes"
          value={
            values?.consignee?.notes || values?.consignee?.consigneeNotes || ''
          }
          onChange={(e) =>
            handleNestedChange('consignee', 'notes', e.target.value)
          }
          placeholder="Type any notes here"
          multiline
          rows={4}
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={6}>
        <CustomSelect
          label="Opening time"
          name="consignee.openingTime"
          value={
            values?.consignee?.businessHours?.open ||
            values?.consignee?.openingTime ||
            ''
          }
          onChange={(field, value) => {
            // Update both the new and old structures for backward compatibility
            handleNestedChange('consignee', 'businessHours', {
              ...(values?.consignee?.businessHours || {}),
              open: value,
            });
            handleNestedChange('consignee', 'openingTime', value);
          }}
          options={timeOptions.map((time) => ({
            id: time,
            value: time,
            label: time,
          }))}
          size="small"
          error={
            touched?.consignee?.openingTime &&
            Boolean(errors?.consignee?.openingTime)
          }
          errorMessage={
            touched?.consignee?.openingTime &&
            Boolean(errors?.consignee?.openingTime)
              ? errors?.consignee?.openingTime
              : undefined
          }
        />
      </Grid>
      <Grid item xs={6}>
        <CustomSelect
          label="Closing time"
          name="consignee.closingTime"
          value={
            values?.consignee?.businessHours?.close ||
            values?.consignee?.closingTime ||
            ''
          }
          onChange={(field, value) => {
            // Update both the new and old structures for backward compatibility
            handleNestedChange('consignee', 'businessHours', {
              ...(values?.consignee?.businessHours || {}),
              close: value,
            });
            handleNestedChange('consignee', 'closingTime', value);
          }}
          options={timeOptions.map((time) => ({
            id: time,
            value: time,
            label: time,
          }))}
          size="small"
          error={
            touched?.consignee?.closingTime &&
            Boolean(errors?.consignee?.closingTime)
          }
          errorMessage={
            touched?.consignee?.closingTime &&
            Boolean(errors?.consignee?.closingTime)
              ? errors?.consignee?.closingTime
              : undefined
          }
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Contact name"
          name="consignee.contactName"
          value={values.consignee?.contactName || ''}
          onChange={(e) =>
            handleNestedChange('consignee', 'contactName', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={8}>
        <TextField
          fullWidth
          label="Phone"
          name="consignee.contactPhone"
          value={
            values.consignee?.contactPhone || values.consignee?.phone || ''
          }
          onChange={(e) =>
            handleNestedChange('consignee', 'contactPhone', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          fullWidth
          label="Ext."
          name="consignee.contactExt"
          value={
            values.consignee?.contactExt || values.consignee?.phoneExt || ''
          }
          onChange={(e) =>
            handleNestedChange('consignee', 'contactExt', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Contact email"
          name="consignee.contactEmail"
          value={
            values.consignee?.contactEmail || values.consignee?.email || ''
          }
          onChange={(e) =>
            handleNestedChange('consignee', 'contactEmail', e.target.value)
          }
          size="small"
          variant="outlined"
        />
      </Grid>
    </Grid>
  </Box>
);

// Helper function to safely format dates
const formatDateSafely = (dateString) => {
  if (!dateString || isNaN(new Date(dateString).getTime())) {
    return '';
  }
  return format(new Date(dateString), 'MM/dd/yyyy hh:mm a');
};

// Time and Service Information Section Component
const TimeServiceSection = ({
  values,
  setFieldValue,
  handleCheckboxChange,
  setTouched,
  touched,
  type,
}) => {
  const timeLabel = type === 'single_shipper' ? 'Pickup Time' : 'Delivery Time';
  const appointmentLabel =
    type === 'single_shipper'
      ? 'Make this an appointment'
      : 'Make this a delivery appointment';
  const confirmLabel =
    type === 'single_shipper'
      ? 'Confirm pickup appointment'
      : 'Confirm delivery appointment';

  const isAppointmentPickup = values?.orderStops[0]?.isAppointmentPickup;
  const isAppointmentDelivery = values?.orderStops[0]?.isAppointmentDelivery;

  // ✅ Move function definition before usage - field selection based only on appointment flags
  const getTimeFields = (isAppointmentPickup, isAppointmentDelivery) => {
    if (isAppointmentPickup) {
      return { start: 'pickUpTimeStart', end: 'pickUpTimeEnd' };
    }
    if (isAppointmentDelivery) {
      return { start: 'requestedTimeStart', end: 'requestedTimeEnd' };
    }
    return { start: 'readyTimeStart', end: 'readyTimeEnd' };
  };

  const fields = getTimeFields(isAppointmentPickup, isAppointmentDelivery);

  const getTimeValues = (
    values,
    isAppointmentPickup,
    isAppointmentDelivery
  ) => {
    const fields = getTimeFields(isAppointmentPickup, isAppointmentDelivery);

    return {
      start:
        values?.orderStops[0]?.[fields.start] ||
        values?.shipper?.[fields.start] ||
        '',
      end:
        values?.orderStops[0]?.[fields.end] ||
        values?.shipper?.[fields.end] ||
        '',
    };
  };

  const updateTimeFields = (
    timeType,
    value,
    setFieldValue,
    setTouched,
    touched
  ) => {
    const fields = getTimeFields(isAppointmentPickup, isAppointmentDelivery);

    if (timeType === 'start') {
      setFieldValue(`orderStops[0].${fields.start}`, value);
      setFieldValue(`orderStops[0].${fields.end}`, value);
      setTouched({
        ...touched,
        [`orderStops[0].${fields.start}`]: true,
        [`orderStops[0].${fields.end}`]: true,
      });
    } else {
      setFieldValue(`orderStops[0].${fields.end}`, value);
      setTouched({
        ...touched,
        [`orderStops[0].${fields.end}`]: true,
      });
    }
  };

  const timeValues = getTimeValues(
    values,
    isAppointmentPickup,
    isAppointmentDelivery
  );

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {isAppointmentPickup || isAppointmentDelivery
          ? timeLabel
          : type === 'single_shipper'
          ? 'Ready Time'
          : 'Available Time'}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
            Start time <span style={{ color: 'red' }}>*</span>
          </Typography>
          <DateTimePicker
            value={formatDateSafely(timeValues.start)}
            onChange={(value) =>
              updateTimeFields(
                'start',
                value,
                setFieldValue,
                setTouched,
                touched
              )
            }
            name={`orderStops[0].${fields.start}`}
            placeholder="MM-DD-YY @ HH:MM"
            variant="outlined"
            disablePast={true}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
            End time <span style={{ color: 'red' }}>*</span>
          </Typography>
          <DateTimePicker
            value={formatDateSafely(timeValues.end)}
            onChange={(value) =>
              updateTimeFields('end', value, setFieldValue, setTouched, touched)
            }
            name={`orderStops[0].${fields.end}`}
            placeholder="MM-DD-YY @ HH:MM"
            variant="outlined"
            disablePast={true}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                name={
                  type === 'single_shipper'
                    ? 'isAppointmentPickup'
                    : 'isAppointmentDelivery'
                }
                checked={
                  type === 'single_shipper'
                    ? isAppointmentPickup || false
                    : isAppointmentDelivery || false
                }
                onChange={handleCheckboxChange}
                size="small"
              />
            }
            label={appointmentLabel}
            sx={{
              '& .MuiFormControlLabel-label': {
                fontSize: '0.875rem',
                fontWeight: 500,
              },
            }}
          />
        </Grid>

        {(isAppointmentPickup || isAppointmentDelivery) && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name={
                    type === 'single_shipper'
                      ? 'isAppointmentConfirmPickup'
                      : 'isAppointmentConfirmDelivery'
                  }
                  checked={
                    type === 'single_shipper'
                      ? values?.orderStops[0]?.isAppointmentConfirmPickup ||
                        false
                      : values?.orderStops[0]?.isAppointmentConfirmDelivery ||
                        false
                  }
                  onChange={handleCheckboxChange}
                  size="small"
                />
              }
              label={confirmLabel}
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                  fontWeight: 500,
                },
              }}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

// Main Component
export default function SingleOrderForm({
  values,
  setFieldValue,
  detailsRef,
  commoditiesRef,
  customersAddresses = [],
  customers = [],
  accessorialsData = [],
  orderId,
  setTouched,
  touched = {},
  errors = {},
  onStopsReorder,
  title,
  type = 'single_shipper', // Default to shipper
}) {
  // console.log('values in single order form', values);
  const [accessorials, setAccessorials] = useState([]);
  const [loadingAccessorials, setLoadingAccessorials] = useState(true);
  const [errorAccessorials, setErrorAccessorials] = useState(null);
  const [showAccessorialDropdown, setShowAccessorialDropdown] = useState(false);
  const accessorialDropdownRef = useRef(null);
  const [stops, setStops] = useState();
  const prevDataRef = useRef();

  const isShipper = type === 'single_shipper';
  const entityType = isShipper ? 'shipper' : 'consignee';

  // Sync information to all orderStops based on type
  useEffect(() => {
    // Only run if we have order stops and entity data
    if (!values?.orderStops?.length || !values?.[entityType]) return;

    // Get the current entity data in a normalized format
    const currentEntity = {
      name: values[entityType]?.name || values[entityType]?.company || '',
      addresses: {
        address:
          values[entityType]?.addresses?.address ||
          values[entityType]?.address?.address ||
          '',
        address2:
          values[entityType]?.addresses?.address2 ||
          values[entityType]?.address?.address2 ||
          '',
        city:
          values[entityType]?.addresses?.city ||
          values[entityType]?.address?.city ||
          '',
        state:
          values[entityType]?.addresses?.state ||
          values[entityType]?.address?.state ||
          '',
        country:
          values[entityType]?.addresses?.country ||
          values[entityType]?.address?.country ||
          '',
        postal:
          values[entityType]?.addresses?.postal ||
          values[entityType]?.address?.postal ||
          '',
        latitude:
          values[entityType]?.addresses?.latitude ||
          values[entityType]?.address?.latitude ||
          '',
        longitude:
          values[entityType]?.addresses?.longitude ||
          values[entityType]?.address?.longitude ||
          '',
      },
      notes: values[entityType]?.notes || '',
      openingTime:
        values[entityType]?.openingTime ||
        values[entityType]?.businessHours?.open ||
        '',
      closingTime:
        values[entityType]?.closingTime ||
        values[entityType]?.businessHours?.close ||
        '',
      contactName: values[entityType]?.contactName || '',
      contactPhone: values[entityType]?.contactPhone || '',
      contactExt: values[entityType]?.contactExt || '',
      contactEmail: values[entityType]?.contactEmail || '',
      // Time fields based on type
      ...(isShipper
        ? {
            readyTimeStart: values.readyTimeStart || values.readyTimeStart,
            readyTimeEnd: values.readyTimeEnd || values.readyTimeStart,
            pickUpTimeStart: values.pickUpTimeStart || values.pickUpTimeStart,
            pickUpTimeEnd: values.pickUpTimeEnd || values.pickUpTimeStart,
          }
        : {
            availableTimeStart:
              values.availableTimeStart || values.availableTimeStart,
            availableTimeEnd:
              values.availableTimeEnd || values.availableTimeStart,
            deliveryTimeStart:
              values.deliveryTimeStart || values.deliveryTimeStart,
            deliveryTimeEnd: values.deliveryTimeEnd || values.deliveryTimeStart,
          }),
    };

    // Skip if entity data hasn't changed
    if (JSON.stringify(prevDataRef.current) === JSON.stringify(currentEntity)) {
      return;
    }

    // Update the ref with current entity data
    prevDataRef.current = currentEntity;

    // Update order stops with the new entity data
    const updatedStops = values.orderStops.map((stop) => ({
      ...stop,
      // Update time fields based on type
      ...(isShipper
        ? {
            readyTimeStart:
              values.readyTimeStart ||
              values.readyTimeStart ||
              stop.readyTimeStart,
            readyTimeEnd:
              values.readyTimeEnd || values.readyTimeStart || stop.readyTimeEnd,
            pickUpTimeStart:
              values.pickUpTimeStart ||
              values.pickUpTimeStart ||
              stop.pickUpTimeStart,
            pickUpTimeEnd:
              values.pickUpTimeEnd ||
              values.pickUpTimeStart ||
              stop.pickUpTimeEnd,
          }
        : {
            availableTimeStart:
              values.availableTimeStart ||
              values.availableTimeStart ||
              stop.availableTimeStart,
            availableTimeEnd:
              values.availableTimeEnd ||
              values.availableTimeStart ||
              stop.availableTimeEnd,
            deliveryTimeStart:
              values.deliveryTimeStart ||
              values.deliveryTimeStart ||
              stop.deliveryTimeStart,
            deliveryTimeEnd:
              values.deliveryTimeEnd ||
              values.deliveryTimeStart ||
              stop.deliveryTimeEnd,
          }),
      [entityType]: {
        ...stop[entityType],
        ...currentEntity,
        addresses: {
          ...stop[entityType]?.addresses,
          ...currentEntity.addresses,
        },
      },
    }));

    // Only update if there are actual changes
    const hasChanges = values.orderStops.some((stop, index) => {
      return (
        JSON.stringify(stop[entityType]) !==
        JSON.stringify(updatedStops[index][entityType])
      );
    });

    if (hasChanges) {
      setFieldValue('orderStops', updatedStops);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.[entityType], values?.orderStops?.length, entityType, isShipper]);

  // Fetch services types
  const { data: servicesTypesData } = useQuery({
    queryKey: ['services'],
    queryFn: () => getServicesActive(),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const serviceTypes = servicesTypesData?.data?.data || [];
  const processedServicesTypes = serviceTypes.map((service) => ({
    id: service.uuid,
    label: service.name,
    value: service.name,
  }));

  // Fetch accessorials
  useEffect(() => {
    const fetchAccessorials = async () => {
      try {
        setLoadingAccessorials(true);
        const data = await getAllAccessorials();
        setAccessorials(data?.data?.data);
      } catch (err) {
        setErrorAccessorials(err);
      } finally {
        setLoadingAccessorials(false);
      }
    };

    fetchAccessorials();
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        accessorialDropdownRef.current &&
        !accessorialDropdownRef.current.contains(event.target)
      ) {
        setShowAccessorialDropdown(false);
      }
    };

    if (showAccessorialDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAccessorialDropdown]);

  // Event handlers
  const handleNestedChange = (section, field, value) => {
    setFieldValue(`${section}.${field}`, value);
  };

  const handleAddressChange = (section, field, value) => {
    setFieldValue(`${section}.addresses.${field}`, value);
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFieldValue(`orderStops[0].${name}`, checked);
  };

  return (
    <Box
      sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      {/* Main Two-Column Layout */}
      {type !== 'sequence' ? (
        <Box sx={{ backgroundColor: 'white', p: 2, borderRadius: 1 }}>
          <Grid container spacing={4}>
            {/* Left Column - Entity Information (Shipper or Consignee) */}
            <Grid item xs={12} md={6}>
              {isShipper ? (
                <ShipperInformationSection
                  values={values}
                  customersAddresses={customersAddresses}
                  handleNestedChange={handleNestedChange}
                  handleAddressChange={handleAddressChange}
                  setFieldValue={setFieldValue}
                  touched={touched}
                  errors={errors}
                />
              ) : (
                <ConsigneeInformationSection
                  values={values}
                  customersAddresses={customersAddresses}
                  handleNestedChange={handleNestedChange}
                  handleAddressChange={handleAddressChange}
                  setFieldValue={setFieldValue}
                  touched={touched}
                  errors={errors}
                />
              )}
            </Grid>
            {/* Divider */}
            <Grid
              item
              xs={12}
              md={0.5}
              sx={{
                display: { xs: 'none', md: 'flex' },
                justifyContent: 'center',
              }}
            >
              <Divider orientation="vertical" flexItem />
            </Grid>
            {/* Right Column - Time and Service Information */}
            <Grid item xs={12} md={5.4} sx={{ pl: 1 }}>
              <TimeServiceSection
                values={values}
                setFieldValue={setFieldValue}
                handleCheckboxChange={handleCheckboxChange}
                setTouched={setTouched}
                touched={touched}
                type={type}
              />
            </Grid>
          </Grid>
        </Box>
      ) : null}
      {/* Stops Section */}
      <StopsComponent
        values={values}
        setFieldValue={setFieldValue}
        setTouched={setTouched}
        detailsRef={detailsRef}
        commoditiesRef={commoditiesRef}
        customersAddresses={customersAddresses}
        customers={customers}
        accessorialsData={accessorialsData}
        orderId={orderId}
        touched={touched}
        errors={errors}
        onStopsReorder={onStopsReorder}
        stops={stops}
        setStops={setStops}
        type={type === 'single_shipper' ? 'shipper' : 'consignee'}
      />

      {/* Commodities Section */}
      {commoditiesRef && values?.orderStops?.length > 0 && (
        <CommiditiesTable
          values={values}
          isMultiple={true}
          commoditiesRef={commoditiesRef}
        />
      )}
    </Box>
  );
}
